import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spin, SelectProps, Button, Divider, Space, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useRegister } from '../../contexts/RegisterContext';
import { UnifiedItemModal, StorageItemBasic } from '@ryo/ui';

const { Text } = Typography;

export type TRegisterType = 'PRODUCT' | 'SERVICE' | 'FIXED_ASSET';

interface ItemSearchSelectProps extends Omit<SelectProps<number>, 'onChange'> {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  itemType?: 'ALL' | 'SELL' | 'PURCHASE' | 'FIXED_ASSET' | 'PRODUCT' | 'SERVICE';
  allowedRegisterTypes?: TRegisterType[];
  excludeServices?: boolean;
  purchaseOnly?: boolean;
  sellOnly?: boolean;
  traceableOnly?: boolean;
  onItemSelect?: (item: any) => void;
  initialItems?: any[];
}

interface RenderDropdownProps {
  menu: React.ReactNode;
  onCreateClick: () => void;
}

const RenderDropdown = ({ menu, onCreateClick }: RenderDropdownProps) => (
  <>
    {menu}
    <Divider style={{ margin: '8px 0' }} />
    <Space style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'center' }}>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCreateClick}
        size="small"
        style={{ width: '100%' }}
      >
        Crear Nuevo Elemento en Catálogo
      </Button>
    </Space>
  </>
);

export const ItemSearchSelect: React.FC<ItemSearchSelectProps> = ({
  value,
  onChange,
  placeholder = 'Buscar elemento en catálogo (producto, insumo, servicio)...',
  itemType = 'ALL',
  allowedRegisterTypes,
  excludeServices = false,
  purchaseOnly = false,
  sellOnly = false,
  traceableOnly = false,
  onItemSelect,
  initialItems = [],
  ...rest
}) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const { securePost } = useSecureRequest();
  const { register } = useRegister();

  const [fetching, setFetching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [createdItems, setCreatedItems] = useState<any[]>([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const fetchOptions = useCallback((query: string = '') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const delay = query.trim() ? 350 : 0;
    timeoutRef.current = setTimeout(async () => {
      if (!company?.current?.uid) return;
      setFetching(true);
      try {
        const filterPayload: any = {
          company: company.current.uid,
          search: query.trim(),
          type: 'full',
          module: config.module,
          hitsPerPage: 30
        };

        if (allowedRegisterTypes && allowedRegisterTypes.length > 0) {
          filterPayload.registerType = allowedRegisterTypes;
        } else if (itemType === 'PRODUCT' || itemType === 'SERVICE' || itemType === 'FIXED_ASSET') {
          filterPayload.registerType = itemType;
        } else if (excludeServices) {
          filterPayload.excludeServices = true;
        }

        if (purchaseOnly || itemType === 'PURCHASE') {
          filterPayload.purchaseItem = true;
        }
        if (sellOnly || itemType === 'SELL') {
          filterPayload.sellItem = true;
        }
        if (traceableOnly) {
          filterPayload.isTraceable = true;
        }

        const response = await securePost('/inventory/item/search-items', filterPayload);
        if (response?.itemList) {
          setSearchResults(response.itemList);
        }
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setFetching(false);
      }
    }, delay);
  }, [
    company?.current?.uid,
    config.module,
    allowedRegisterTypes,
    itemType,
    excludeServices,
    purchaseOnly,
    sellOnly,
    traceableOnly,
    securePost
  ]);

  const currentCompanyUid = company?.current?.uid;

  useEffect(() => {
    setSearchResults([]);
    setCreatedItems([]);
    if (company?.current?.uid) {
      fetchOptions('');
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentCompanyUid, fetchOptions]);

  const combinedDataMap = new Map();

  // 1. Initial items passed as prop
  initialItems.forEach(item => {
    if (item?.id !== undefined) combinedDataMap.set(item.id, item);
  });

  // 2. Search results from API
  searchResults.forEach(item => {
    if (item?.id !== undefined) combinedDataMap.set(item.id, item);
  });

  // 3. Created items
  createdItems.forEach(item => {
    if (item?.id !== undefined) combinedDataMap.set(item.id, item);
  });

  // Ensure selected value is preserved in map
  if (value && !combinedDataMap.has(value)) {
    const selectedInitial = initialItems.find(i => i.id === value) || createdItems.find(i => i.id === value);
    if (selectedInitial) {
      combinedDataMap.set(value, selectedInitial);
    }
  }

  const data = Array.from(combinedDataMap.values()).filter(i => {
    // 1. Filtro por compañía activa
    if (currentCompanyUid && i.company_uid && i.company_uid !== currentCompanyUid) return false;

    // 2. Filtro de trazabilidad
    if (traceableOnly && !i.is_traceable && i.id !== value) return false;

    // 3. Excluir servicios si está habilitado
    if (excludeServices && i.register_type === 'SERVICE' && i.id !== value) return false;

    // 4. Filtro por lista de tipos de registro permitidos
    if (allowedRegisterTypes && allowedRegisterTypes.length > 0 && !allowedRegisterTypes.includes(i.register_type) && i.id !== value) {
      return false;
    }

    // 5. Filtro por itemType
    if (itemType === 'PRODUCT' && i.register_type !== 'PRODUCT' && i.id !== value) return false;
    if (itemType === 'SERVICE' && i.register_type !== 'SERVICE' && i.id !== value) return false;
    if (itemType === 'FIXED_ASSET' && i.register_type !== 'FIXED_ASSET' && i.id !== value) return false;
    if (itemType === 'SELL' && !i.sell_item && i.id !== value) return false;
    if (itemType === 'PURCHASE' && !i.purchase_item && i.id !== value) return false;

    // 6. Filtros por capacidad de compra/venta
    if (purchaseOnly && !i.purchase_item && i.id !== value) return false;
    if (sellOnly && !i.sell_item && i.id !== value) return false;

    return true;
  });

  const handleChange = (val: number) => {
    if (onChange) onChange(val);
    if (onItemSelect) {
      const selectedItem = data.find(i => i.id === val);
      onItemSelect(selectedItem);
    }
  };

  const handleSaveItem = async (values: StorageItemBasic): Promise<any> => {
    if (!company?.current?.uid || !company.current?.id) {
      throw new Error('No se encontró la compañía activa');
    }

    const isService = values.register_type === 'SERVICE';
    const prices: any[] = [];
    if (values.sell_price !== undefined && values.sell_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'SELL',
        value: values.sell_price,
        description: 'Precio de venta',
        is_active: true,
        is_default: true,
        is_visible: true,
      });
    }
    if (values.purchase_price !== undefined && values.purchase_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'PURCHASE',
        value: values.purchase_price,
        description: 'Precio de compra',
        is_active: true,
        is_default: true,
        is_visible: true,
      });
    }

    const moduleOptions: any[] = [];
    const categoryKey = values.category || 'VRS';
    const displayKey = isService ? 'N/A' : 'UND';

    const itemTypes = register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || []);
    const categoryOptionsList = register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || []);
    const displayOptionsList = register.displayOptions?.length ? register.displayOptions : (company.moduleOptions?.filter((el: any) => el.type === 'DISPLAY_OPTION') || []);
    const umOptionsList = register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || []);

    const idItemType = itemTypes?.find((el: any) => el.key === values.register_type)?.id;
    const idCategory = categoryOptionsList?.find((el: any) => el.key === categoryKey)?.id;
    const idDisplay = displayOptionsList?.find((el: any) => el.key === displayKey)?.id;
    const idUm = umOptionsList?.find((el: any) => el.key === (isService ? 'UND' : values.measure_unit) || (values.measure_unit === 'UN' && el.key === 'UND'))?.id;

    if (idItemType) {
      moduleOptions.push({
        id: -1,
        option_id: idItemType,
        is_active: true,
        sub_type: 'TYPE_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idCategory) {
      moduleOptions.push({
        id: -1,
        option_id: idCategory,
        is_active: true,
        sub_type: 'CATEGORY_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idDisplay) {
      moduleOptions.push({
        id: -1,
        option_id: idDisplay,
        is_active: true,
        sub_type: 'DISPLAY_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idUm) {
      moduleOptions.push({
        id: -1,
        option_id: idUm,
        is_active: true,
        sub_type: 'UM_ELEMENT',
        category: 'NATIONAL',
      });
    }

    const storageElement: any = {
      company_id: company.current.id,
      company_uid: company.current.uid,
      name: values.name,
      description: values.description || '',
      is_active: true,
      is_traceable: isService ? false : (traceableOnly ? true : Boolean(values.is_traceable)),
      sell_item: values.sell_item ?? true,
      purchase_item: values.purchase_item ?? false,
      register_type: values.register_type,
      measure_unit: isService ? 'UN' : (values.measure_unit || 'UN'),
      category: values.category || 'VRS',
      display: isService ? 'N/A' : 'UND',
      location: isService ? 'N/A' : '',
      image: '',
      image_mini: '',
      quantity: 0,
      sell_price: values.sell_price || 0,
      purchase_price: values.purchase_price || 0,
      sell_discount: 0,
      purchase_discount: 0,
      custom_id: values.custom_id || '',
      sku: values.sku || '',
      barcode: '',
      module_options: moduleOptions,
      prices,
      other_values: [],
    };

    const response = await securePost('/inventory/item/create-item', {
      company: company.current.uid,
      storageElement,
    });

    if (response?.item || response?.storageElement) {
      const savedItem = response.item || response.storageElement;
      setCreatedItems(prev => [savedItem, ...prev]);
      handleChange(savedItem.id);
      return savedItem;
    } else {
      throw new Error(response?.message || 'Error al guardar el elemento');
    }
  };

  const itemTypeOptions = (register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || [])).filter((o: any) => o.show);
  const umOptions = (register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || [])).filter((o: any) => o.show);
  const categoryOptions = (register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || [])).filter((o: any) => o.show);
  const displayOptions = (register.displayOptions?.length ? register.displayOptions : (company.moduleOptions?.filter((el: any) => el.type === 'DISPLAY_OPTION') || [])).filter((o: any) => o.show);

  return (
    <>
      <Select
        showSearch
        allowClear
        value={value}
        placeholder={placeholder}
        filterOption={(input, option) => {
          const item = data.find(i => i.id === option?.value);
          if (!item) return false;
          const searchLower = input.toLowerCase();
          const nameMatch = (item.name || '').toLowerCase().includes(searchLower);
          const skuMatch = (item.sku || '').toLowerCase().includes(searchLower);
          const customIdMatch = (item.custom_id || '').toLowerCase().includes(searchLower);
          const barcodeMatch = (item.barcode || '').toLowerCase().includes(searchLower);
          const categoryLabel = categoryOptions.find((c: any) => c.key === item.category)?.string_value || item.category || '';
          const categoryMatch = categoryLabel.toLowerCase().includes(searchLower);
          return nameMatch || skuMatch || customIdMatch || barcodeMatch || categoryMatch;
        }}
        onSearch={fetchOptions}
        onChange={handleChange}
        notFoundContent={fetching ? <div style={{ textAlign: 'center', padding: '12px' }}><Spin size="small" /> Buscando...</div> : 'No se encontraron resultados'}
        popupRender={(menu) => (
          <RenderDropdown
            menu={menu}
            onCreateClick={() => setShowItemModal(true)}
          />
        )}
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        styles={{ popup: { root: { minWidth: 380, maxWidth: 600, maxHeight: 380 } } }}
        optionLabelProp="label"
        {...rest}
      >
        {data.map((item) => {
          const isService = item.register_type === 'SERVICE';
          const isFixedAsset = item.register_type === 'FIXED_ASSET';
          const categoryLabel = categoryOptions.find((c: any) => c.key === item.category)?.string_value || item.category;
          const displayLabel = displayOptions.find((d: any) => d.key === item.display)?.string_value || 
                               umOptions.find((u: any) => u.key === item.measure_unit)?.string_value || 
                               item.display || 
                               item.measure_unit || 
                               'UND';
          const code = item.custom_id || item.sku;
          const cleanLabel = `${item.name}${code ? ` (#${code})` : ''}${item.display || item.measure_unit ? ` - ${item.display || item.measure_unit}` : ''}`;
          const totalStock = Array.isArray(item.inventory)
            ? item.inventory.reduce((sum: number, inv: any) => sum + (Number(inv.quantity) || 0), 0)
            : (Number(item.quantity) || 0);
          const refPrice = item.purchase_price ?? item.sell_price;

          return (
            <Select.Option key={item.id} value={item.id} label={cleanLabel} style={{ padding: '6px 8px', height: 'auto', maxWidth: '100%' }}>
              <div style={{ whiteSpace: 'normal', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '100%', overflow: 'hidden' }}>
                {/* Title & Tag */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 13, color: '#1f1f1f', lineHeight: '1.3', flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word' }}>
                    {item.name}
                  </Text>
                  <Tag 
                    color={isService ? 'geekblue' : isFixedAsset ? 'purple' : 'blue'} 
                    style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 5px', fontWeight: 500, flexShrink: 0 }}
                  >
                    {isService ? 'Servicio' : isFixedAsset ? 'Activo Fijo' : 'Producto'}
                  </Tag>
                </div>

                {/* Metadata row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 2 }}>
                  {code && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
                      Ref: <strong>{code}</strong>
                    </Tag>
                  )}
                  {categoryLabel && categoryLabel !== 'VRS' && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
                      Cat: {categoryLabel}
                    </Tag>
                  )}
                  {(item.display || item.measure_unit) && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#e6f7ff', color: '#096dd9', padding: '0 4px' }}>
                      Pres: <strong>{item.display || displayLabel}</strong>
                    </Tag>
                  )}
                  {!isService && (
                    <span style={{ 
                      fontSize: 10, 
                      color: totalStock > 0 ? '#389e0d' : '#cf1322', 
                      background: totalStock > 0 ? '#f6ffed' : '#fff1f0',
                      border: `1px solid ${totalStock > 0 ? '#b7eb8f' : '#ffa39e'}`,
                      borderRadius: 3,
                      padding: '0 4px',
                      fontWeight: 600
                    }}>
                      Stock: {totalStock} {item.display || item.measure_unit || 'UND'}
                    </span>
                  )}
                  {refPrice !== undefined && refPrice !== null && Number(refPrice) > 0 && (
                    <span style={{ fontSize: 10, color: '#389e0d', fontWeight: 600, background: '#f6ffed', padding: '0 4px', borderRadius: 3, border: '1px solid #b7eb8f' }}>
                      Ref: ${Number(refPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>

                {/* Description snippet */}
                {item.description && (
                  <div 
                    style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1, whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} 
                    title={item.description}
                  >
                    {item.description}
                  </div>
                )}
              </div>
            </Select.Option>
          );
        })}
      </Select>

      <UnifiedItemModal
        visible={showItemModal}
        onCancel={() => setShowItemModal(false)}
        onSuccess={(savedItem) => {
          setShowItemModal(false);
          setCreatedItems(prev => [savedItem, ...prev]);
          handleChange(savedItem.id);
        }}
        onSave={handleSaveItem}
        itemTypes={itemTypeOptions}
        umOptions={umOptions}
        categoryOptions={categoryOptions}
      />
    </>
  );
};

export default ItemSearchSelect;
